import React from 'react';

import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { buildCoverFields, splitCoverColumns } from './coverData';
import { emptyPeriodMessage } from './monthly';
import { CONTENT_WIDTH, FONT, FONT_BOLD, PdfTable } from './pdfTable';
import reportLogo from '../../../assets/logo-report.png';
import { formatIsoToDate, money } from '../../../utils';

// PRST-134 — the "Earnings History" packet a subpoena response is built from.
//
// Page 1 is the cover data (plus an empty-period notice when there are no checks, PRST-140), then
// ONE PAGE PER CALENDAR MONTH of checks. Separate <Page> elements
// (rather than one <Page> with manual breaks) make "one summarized month per page" structural:
// two months can never share a sheet, and a month too busy for one sheet still spills onto a
// continuation sheet with the header and footer intact. Page numbering stays continuous across
// all of them because react-pdf's wrapPages() accumulates subpages across every <Page> child with
// a single running count — pageNumber/totalPages are document-global, not per-<Page>.
//
// Styling is StyleSheet.create, not scoped CSS: react-pdf paints to a canvas and cannot resolve
// var(--primary). No brand colour is used at all — the spec is black text on #D7D7D7 and the logo
// carries the brand, which also avoids inheriting the wrong-for-both-brands #c8102e literal the
// other two PDFs in this repo hardcode.

const PAGE_MARGIN = 72; // 1 inch, per the ticket's header/footer margin spec.
const FOOTER_BLOCK_HEIGHT = 72;

export const styles = StyleSheet.create({
  page: {
    paddingTop: PAGE_MARGIN,
    paddingHorizontal: PAGE_MARGIN,
    // Reserves the footer band: react-pdf cuts the flow at paddingBottom, so without this the
    // body runs underneath the (absolutely positioned) footer on every page.
    paddingBottom: PAGE_MARGIN + FOOTER_BLOCK_HEIGHT,
    fontSize: 9,
    fontFamily: FONT,
    color: '#000000',
  },
  // minHeight keeps the rule below the absolutely-positioned logo: the logo is taken out of flow,
  // so without it the two centred text lines alone set the header height and the rule cuts through
  // the artwork.
  header: { marginBottom: 16, minHeight: 44 },
  headerLogoWrap: { position: 'absolute', left: 0, top: 0 },
  // 100pt wide (≈26pt tall at the logo's aspect) leaves the centred "Payroll Dates Between…" line
  // clear of the artwork. At 150pt the two collided.
  logo: { width: 100 },
  headerTitle: { fontSize: 14, fontFamily: FONT_BOLD, textAlign: 'center' },
  headerSubtitle: { fontSize: 9, textAlign: 'center', marginTop: 3 },
  headerRule: { borderTop: 1, borderColor: '#000000', marginTop: 10 },

  // Calibrated against a rendered PDF, not inferred: in react-pdf 1.6.11 an absolutely positioned
  // child is offset from the page's BORDER box (the paper edge), not its padding box. So the
  // one-inch margins the ticket specifies for the footer have to be stated here explicitly —
  // left/right/bottom: 0 put the confidential paragraph edge-to-edge and flush to the paper
  // bottom, and a negative offset pushed it off the sheet entirely without drawing anything.
  footer: { position: 'absolute', bottom: PAGE_MARGIN, left: PAGE_MARGIN, right: PAGE_MARGIN },
  footerLabel: { fontSize: 8, textAlign: 'center' },
  footerText: { fontSize: 8, textAlign: 'center', marginTop: 2 },
  footerBold: { fontFamily: FONT_BOLD },
  pageNumber: { fontSize: 8, textAlign: 'right', marginTop: 6 },

  coverBox: { flexDirection: 'row', borderWidth: 1, borderColor: '#cccccc', padding: 12, marginTop: 4 },
  coverColumn: { width: '50%', paddingRight: 8 },
  coverRow: { flexDirection: 'row', marginBottom: 5 },
  coverLabel: { width: 118, fontFamily: FONT_BOLD, fontSize: 7.5 },
  coverValue: { flex: 1, fontSize: 7.5 },

  monthHeading: { fontSize: 10, fontFamily: FONT_BOLD, marginBottom: 2 },
  sectionHeading: { fontSize: 10, fontFamily: FONT_BOLD, marginTop: 14 },
  // PRST-140 empty-period notice under the cover box. Bold via the font FAMILY — fontWeight is a
  // no-op for the standard fonts in 1.6.11.
  emptyPeriod: { fontSize: 9, fontFamily: FONT_BOLD, marginTop: 14 },
});

const CONFIDENTIAL_TEXT =
  'This report contains information that is confidential or proprietary, and intended for the ' +
  'sole use of TrueBlue or its affiliates.  This information is subject to revision or ' +
  'correction at any time without prior notice to any user.';

// `fixed` makes this repeat on every sheet. It is a FLOW child (not absolutely positioned) so
// Yoga re-lays it out at the top of each page and pushes the body down — no overlap to manage.
export const ReportHeader = ({ criteria = {} }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerLogoWrap}>
      <Image source={reportLogo} style={styles.logo} />
    </View>
    <Text style={styles.headerTitle}>Earnings History</Text>
    <Text style={styles.headerSubtitle}>
      Payroll Dates Between {formatIsoToDate(criteria.FromPerEnddate)} and{' '}
      {formatIsoToDate(criteria.ToPerEnddate)}
    </Text>
    <View style={styles.headerRule} />
  </View>
);

export const ReportFooter = () => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerLabel}>Additional Information:</Text>
    <Text style={styles.footerText}>
      <Text style={styles.footerBold}>CONFIDENTIAL INFORMATION: </Text>
      {CONFIDENTIAL_TEXT}
    </Text>
    {/* `fixed` is required on the Text itself, not just on the wrapping View. During wrap(),
        react-pdf nulls props.render on any node that is not itself fixed before cloning it onto
        the next page, which permanently bakes in "Page 2 of undefined". */}
    <Text
      style={styles.pageNumber}
      fixed
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
  </View>
);

// Two columns, same split as the results screen (splitCoverColumns), because the ticket
// asks for page 1 to be "display of data from View Report page in same format".
const CoverColumn = ({ fields }) => (
  <View style={styles.coverColumn}>
    {fields.map(field => (
      <View style={styles.coverRow} key={field.label} wrap={false}>
        <Text style={styles.coverLabel}>{field.label}</Text>
        <Text style={styles.coverValue}>{field.value}</Text>
      </View>
    ))}
  </View>
);

const CoverBlock = ({ fields = [] }) => {
  const { left, right } = splitCoverColumns(fields);
  return (
    <View style={styles.coverBox}>
      <CoverColumn fields={left} />
      <CoverColumn fields={right} />
    </View>
  );
};

// Accounting format, per the mockup: negatives print in parentheses — "($ 332.94)", never
// "-$332.94". Taxes and deductions are stored negative, so every one of them renders bracketed.
const amount = value => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  const formatted = money.format(Math.abs(number));
  return number < 0 ? `(${formatted})` : formatted;
};
const hours = value => (value === null || value === undefined || value === '' ? '' : Number(value).toFixed(2));

// Widths are points and must total CONTENT_WIDTH (468) — pdfTable.test.js asserts it. Company is
// the widest because the value is a full legal entity name ("Centerline Drivers, LLC", and the
// longer "PeopleReady Skilled Trades" on the PRST build); at 55pt it wrapped to two lines.
export const payDetailColumns = [
  { key: 'Company', header: 'Company', width: 112 },
  { key: 'Branch', header: 'Branch', width: 36 },
  { key: 'GrossPay', header: 'Gross Pay', width: 62, numeric: true, render: row => amount(row.GrossPay) },
  { key: 'NetPay', header: 'Net Pay', width: 62, numeric: true, render: row => amount(row.NetPay) },
  { key: 'DocumentType', header: 'Document Type', width: 68 },
  { key: 'CheckNumber', header: 'Check Number', width: 66 },
  { key: 'CheckDate', header: 'Check Date', width: 62, render: row => formatIsoToDate(row.CheckDate) },
];

export const detailColumns = [
  { key: 'transactionType', header: 'Transaction Type', width: 90 },
  { key: 'transactionDetail', header: 'Transaction Detail', width: 188 },
  { key: 'amount', header: 'Amount', width: 95, numeric: true, render: row => amount(row.amount) },
  { key: 'hours', header: 'Hours', width: 95, numeric: true, render: row => hours(row.hours) },
];

// Earnings rows, then the Earnings Total line, then Taxes, then Deductions — the ticket asks for
// "a total line for each Transaction Type", inline rather than collected at the bottom.
const detailRowsWithTotals = month => {
  const sections = [
    ['Earnings', month.detail.Earnings, month.totals.earnings, 'Earnings Total'],
    ['Tax', month.detail.Tax, month.totals.taxes, 'Tax Total'],
    ['Deduction', month.detail.Deduction, month.totals.deductions, 'Deduction Total'],
  ];
  const rows = [];
  sections.forEach(([type, items, total, totalLabel]) => {
    if (!items.length) return;
    items.forEach((item, index) => rows.push({ ...item, key: `${type}-${index}` }));
    // Per the mockup the total line carries its label in the Transaction TYPE column and leaves
    // Transaction Detail blank — the opposite of where it reads naturally, so don't "fix" it.
    rows.push({
      key: `${type}-total`,
      transactionType: totalLabel,
      transactionDetail: '',
      amount: total,
      hours: month.totalHours?.[type] ?? null,
      isTotal: true,
      spacerAfter: true,
    });
  });
  return rows;
};

export const MonthPage = ({ criteria, workerId, month }) => (
  <Page size="LETTER" style={styles.page}>
    <ReportHeader criteria={criteria} />
    <Text style={styles.monthHeading}>Worker ID Number: {workerId}</Text>
    <Text style={styles.monthHeading}>Payroll Month / Year: {month.label}</Text>

    <Text style={styles.sectionHeading}>Pay Detail Table</Text>
    <PdfTable columns={payDetailColumns} rows={month.checks} />

    <Text style={styles.sectionHeading}>Detail Table</Text>
    <PdfTable columns={detailColumns} rows={detailRowsWithTotals(month)} />

    <ReportFooter />
  </Page>
);

const PayrollVerificationDocument = ({ criteria = {}, cover = {}, months = [], reportDate }) => {
  const fields = buildCoverFields(cover, reportDate || new Date());
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <ReportHeader criteria={criteria} />
        <CoverBlock fields={fields} />
        {/* PRST-140: with no month pages the cover page IS the packet, so it has to say so —
            a cover-only PDF otherwise reads as a complete subpoena response. */}
        {months.length === 0 && <Text style={styles.emptyPeriod}>{emptyPeriodMessage(criteria)}</Text>}
        <ReportFooter />
      </Page>
      {months.map(month => (
        <MonthPage key={month.key} criteria={criteria} workerId={cover?.WorkerId} month={month} />
      ))}
    </Document>
  );
};

export { PAGE_MARGIN, CONTENT_WIDTH, CONFIDENTIAL_TEXT };
export default PayrollVerificationDocument;

// react-pdf hyphenates by default, which split a worker's city across lines as "Ar-lington" on
// the cover page. A hyphenated address on a subpoena response reads as a transcription error.
//
// The only control 1.6.11 offers is Font.registerHyphenationCallback, which is GLOBAL — setting
// it permanently would also change the QuoteEngine and DQF AI Review documents, which are not
// this ticket's to change. So it is set for the duration of one render and restored after,
// using the matching getHyphenationCallback.
export const withoutHyphenation = async render => {
  const previous = typeof Font.getHyphenationCallback === 'function' ? Font.getHyphenationCallback() : null;
  Font.registerHyphenationCallback(word => [word]);
  try {
    return await render();
  } finally {
    if (previous) Font.registerHyphenationCallback(previous);
  }
};
